/**
 * Migration Feature Flags
 *
 * Feature flags to control the gradual migration from AWS Amplify to AWS SDK.
 * These flags allow us to enable/disable specific migration phases independently.
 *
 * Usage:
 *   - Set flags to true to enable migrated SDK code
 *   - Set flags to false to use legacy Amplify code
 *   - Use environment variables to control flags per environment
 */

export interface MigrationFlags {
  useAwsSdkConfig: boolean;

  useAwsSdkAuth: boolean;

  useAwsSdkGraphQL: boolean;

  useAwsSdkApiRoutes: boolean;

  useAwsSdkStorage: boolean;

  useAwsSdkRepositories: boolean;

  enableMigration: boolean;
}

/**
 * Get migration flags from environment variables
 */
function getMigrationFlagsFromEnv(): Partial<MigrationFlags> {
  if (typeof process === 'undefined' || !process.env) {
    return {};
  }

  return {
    useAwsSdkConfig: process.env.NEXT_PUBLIC_USE_AWS_SDK_CONFIG === 'true',
    useAwsSdkAuth: process.env.NEXT_PUBLIC_USE_AWS_SDK_AUTH === 'true',
    useAwsSdkGraphQL: process.env.NEXT_PUBLIC_USE_AWS_SDK_GRAPHQL === 'true',
    useAwsSdkApiRoutes: process.env.NEXT_PUBLIC_USE_AWS_SDK_API_ROUTES === 'true',
    useAwsSdkStorage: process.env.NEXT_PUBLIC_USE_AWS_SDK_STORAGE === 'true',
    useAwsSdkRepositories: process.env.NEXT_PUBLIC_USE_AWS_SDK_REPOSITORIES === 'true',
    enableMigration: process.env.NEXT_PUBLIC_ENABLE_MIGRATION === 'true',
  };
}

/**
 * Default migration flags
 *
 * migration-base branch: All AWS SDK features are enabled
 * These flags enable the direct AWS SDK implementations
 */
const defaultFlags: MigrationFlags = {
  useAwsSdkConfig: true,
  useAwsSdkAuth: true,
  useAwsSdkGraphQL: true,
  useAwsSdkApiRoutes: true,
  useAwsSdkStorage: true,
  useAwsSdkRepositories: true,
  enableMigration: true,
};

/**
 * Current migration flags
 * Environment variables override defaults
 */
export const migrationFlags: MigrationFlags = {
  ...defaultFlags,
  ...getMigrationFlagsFromEnv(),
};

/**
 * Check if a specific migration phase is enabled
 */
export function isMigrationPhaseEnabled(phase: keyof MigrationFlags): boolean {
  if (!migrationFlags.enableMigration) {
    return false;
  }

  return migrationFlags[phase] === true;
}

/**
 * Get all enabled migration phases
 */
export function getEnabledMigrationPhases(): string[] {
  if (!migrationFlags.enableMigration) {
    return [];
  }

  return Object.keys(migrationFlags).filter(
    (key) => key !== 'enableMigration' && migrationFlags[key as keyof MigrationFlags] === true
  );
}

/**
 * Log current migration status
 */
export function logMigrationStatus(): void {
  if (typeof window === 'undefined') {
    return; // Only log on client side
  }

  console.group('🔄 AWS SDK Migration Status');
  console.log('Global migration enabled:', migrationFlags.enableMigration);

  if (migrationFlags.enableMigration) {
    console.log('Enabled phases:', getEnabledMigrationPhases());
    console.table({
      Config: migrationFlags.useAwsSdkConfig ? '✓' : '✗',
      Auth: migrationFlags.useAwsSdkAuth ? '✓' : '✗',
      GraphQL: migrationFlags.useAwsSdkGraphQL ? '✓' : '✗',
      'API Routes': migrationFlags.useAwsSdkApiRoutes ? '✓' : '✗',
      Storage: migrationFlags.useAwsSdkStorage ? '✓' : '✗',
      Repositories: migrationFlags.useAwsSdkRepositories ? '✓' : '✗',
    });
  } else {
    console.log('Migration is disabled. Using Amplify.');
  }

  console.groupEnd();
}

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  logMigrationStatus();
}
